/** @format */

import {
  DestroyRef,
  Directive,
  ElementRef,
  PLATFORM_ID,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const FADE_MASK = `
   linear-gradient(
     to right,
     transparent,

     /* Left fade */
     rgba(0, 0, 0, 0.15)  calc(var(--scroll-fade-width) * var(--scroll-fade-left) * 0.2),
     rgba(0, 0, 0, 0.70)  calc(var(--scroll-fade-width) * var(--scroll-fade-left) * 0.6),

     /* Visible center */
     black                calc(var(--scroll-fade-width) * var(--scroll-fade-left)),
     black                calc(100% - var(--scroll-fade-width) * var(--scroll-fade-right)),

     /* Right fade */
     rgba(0, 0, 0, 0.70)  calc(100% - var(--scroll-fade-width) * var(--scroll-fade-right) * 0.6),
     rgba(0, 0, 0, 0.15)  calc(100% - var(--scroll-fade-width) * var(--scroll-fade-right) * 0.2),

     transparent
   )
 `;

@Directive({
  selector: '[sharedUtilsDirectiveScrollGradientX]',
})
export class ScrollGradientXDirective {
  readonly fadeWidth = input('48px');
  readonly enabled = input(true);

  private readonly el = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private scrollUnlisten?: () => void;
  private resizeObserver?: ResizeObserver;
  private queuedFrame = 0;
  private fadeWidthPx = 48;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    effect(() => {
      const isEnabled = this.enabled();
      const fw = this.fadeWidth();

      untracked(() => {
        if (isEnabled) {
          this.fadeWidthPx = Number.parseFloat(fw);
          this.el.style.setProperty('--scroll-fade-width', fw);
          this.ensureListeners();
          this.scheduleUpdate();
        } else {
          this.detach();
        }
      });
    });

    this.destroyRef.onDestroy(() => this.detach());
  }

  private ensureListeners(): void {
    if (this.scrollUnlisten) return;

    this.el.style.setProperty('-webkit-mask', FADE_MASK);
    this.el.style.setProperty('mask', FADE_MASK);
    this.el.style.setProperty('--scroll-fade-left', '0');
    this.el.style.setProperty('--scroll-fade-right', '0');

    this.el.addEventListener('scroll', this.scheduleUpdate, { passive: true });
    this.scrollUnlisten = () => this.el.removeEventListener('scroll', this.scheduleUpdate);

    this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
    this.resizeObserver.observe(this.el);
  }

  private detach(): void {
    this.scrollUnlisten?.();
    this.scrollUnlisten = undefined;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    cancelAnimationFrame(this.queuedFrame);
    this.queuedFrame = 0;
    this.el.style.removeProperty('-webkit-mask');
    this.el.style.removeProperty('mask');
    this.el.style.removeProperty('--scroll-fade-left');
    this.el.style.removeProperty('--scroll-fade-right');
    this.el.style.removeProperty('--scroll-fade-width');
  }

  private readonly scheduleUpdate = (): void => {
    if (this.queuedFrame) return;
    this.queuedFrame = requestAnimationFrame(this.updateFades);
  };

  private readonly updateFades = (): void => {
    this.queuedFrame = 0;

    const { scrollLeft, scrollWidth, clientWidth } = this.el;
    const fw = this.fadeWidthPx;
    const furthestScroll = scrollWidth - clientWidth;
    const hasOverflow = furthestScroll > 1;

    const leftStrength = hasOverflow ? Math.min(scrollLeft / fw, 1) : 0;
    const rightStrength = hasOverflow ? Math.min((furthestScroll - scrollLeft) / fw, 1) : 0;

    this.el.style.setProperty('--scroll-fade-left', `${leftStrength}`);
    this.el.style.setProperty('--scroll-fade-right', `${rightStrength}`);
  };
}
